import React from "react";
import { motion } from "framer-motion";
import { useLang } from "../context/LanguageContext";
import PageHero from "../components/PageHero";
import ConsultForm from "../components/ConsultForm";
import CTABand from "../components/resource/CTABand";
import { container, item, inView } from "../components/service/presets";
import { Cloud, Layers, Server, LifeBuoy, ShieldCheck, Activity, Check } from "lucide-react";

const ICON_ORDER = [Cloud, Layers, Server, LifeBuoy, ShieldCheck, Activity];

const CONTENT = {
  en: {
    tag: "Cloud & Data Center",
    title: "Cloud and Data Center Services",
    desc: "We design, deploy and operate resilient cloud and data center environments — engineered for scalability, security and continuous availability.",
    intro:
      "From migration and modernization to day-to-day operations, RoboQuess helps enterprises run their infrastructure with confidence. We architect secure, high-performance cloud and hybrid environments, modernize legacy data centers, and keep everything monitored, optimized and compliant — so your teams can focus on growth instead of maintenance.",
    offeringsTitle: "What we deliver",
    offerings: [
      { title: "Cloud Migration & Modernization", desc: "Plan and execute seamless migrations of applications and workloads to the cloud with minimal disruption and clear ROI." },
      { title: "Hybrid & Multi-Cloud", desc: "Unify public and private cloud across providers for flexibility, resilience and optimized cost." },
      { title: "Data Center Management", desc: "Design, build and operate modern data centers with efficient capacity, power and performance management." },
      { title: "Disaster Recovery & Backup", desc: "Protect critical systems with automated backups, replication and regularly tested recovery strategies." },
      { title: "Security & Compliance", desc: "Harden environments with layered security, access governance and adherence to regional compliance." },
      { title: "24/7 Monitoring & Optimization", desc: "Proactive monitoring, tuning and support to keep infrastructure stable, fast and cost-efficient." },
    ],
    benefitsTitle: "Why RoboQuess",
    benefits: ["Scalability on demand", "Lower infrastructure costs", "High availability & uptime", "Enterprise-grade security", "Faster, safer deployments", "Expert managed support"],
    value: "The value",
  },
  ar: {
    tag: "السحابة ومراكز البيانات",
    title: "خدمات السحابة ومراكز البيانات",
    desc: "نصمّم وننشر وندير بيئات سحابية ومراكز بيانات مرنة — مهندسة لتحقيق قابلية التوسّع والأمان والتوافر المستمر.",
    intro:
      "من الترحيل والتحديث إلى العمليات اليومية، تساعد روبوكويس المؤسسات على تشغيل بنيتها التحتية بثقة. نصمّم بيئات سحابية وهجينة آمنة وعالية الأداء، ونحدّث مراكز البيانات التقليدية، ونبقي كل شيء مُراقبًا ومحسّنًا ومتوافقًا مع الأنظمة — حتى تركّز فرقك على النمو بدلًا من الصيانة.",
    offeringsTitle: "ما نقدّمه",
    offerings: [
      { title: "ترحيل السحابة والتحديث", desc: "نخطّط وننفّذ ترحيلًا سلسًا للتطبيقات وأحمال العمل إلى السحابة بأقل انقطاع وبعائد واضح." },
      { title: "السحابة الهجينة والمتعدّدة", desc: "نوحّد السحابة العامة والخاصة عبر مزوّدين مختلفين لتحقيق المرونة والموثوقية وخفض التكلفة." },
      { title: "إدارة مراكز البيانات", desc: "نصمّم ونبني وندير مراكز بيانات حديثة مع إدارة فعّالة للسعة والطاقة والأداء." },
      { title: "التعافي من الكوارث والنسخ الاحتياطي", desc: "نحمي الأنظمة الحرجة عبر نسخ احتياطي آلي وتكرار واستراتيجيات تعافٍ مُختبرة بانتظام." },
      { title: "الأمان والامتثال", desc: "نعزّز البيئات بأمان متعدّد الطبقات وحوكمة للوصول والتزام بالأنظمة المحلية." },
      { title: "المراقبة والتحسين على مدار الساعة", desc: "مراقبة استباقية وضبط ودعم للحفاظ على بنية تحتية مستقرّة وسريعة واقتصادية." },
    ],
    benefitsTitle: "لماذا روبوكويس",
    benefits: ["قابلية التوسّع عند الطلب", "خفض تكاليف البنية التحتية", "توافر عالٍ ووقت تشغيل مرتفع", "أمان بمستوى المؤسسات", "عمليات نشر أسرع وأكثر أمانًا", "دعم مُدار من خبراء"],
    value: "القيمة",
  },
};

export default function CloudDataCenter() {
  const { lang, dir } = useLang();
  const c = CONTENT[lang] || CONTENT.en;

  return (
    <div>
      <PageHero tag={c.tag} title={c.title} desc={c.desc} />

      {/* Intro + sticky form */}
      <section className="py-12">
        <div className="rq-container grid lg:grid-cols-3 gap-10 items-start">
          <motion.div {...inView} variants={container} className="lg:col-span-2">
            <motion.p variants={item} className="text-[15px] md:text-[17px] leading-[1.95] text-[color:var(--rq-muted)]">
              {c.intro}
            </motion.p>
          </motion.div>
          <div className="lg:col-span-1">
            <div className="lg:sticky lg:top-24">
              <ConsultForm source="Cloud & Data Center Services" />
            </div>
          </div>
        </div>
      </section>

      {/* Offerings */}
      <section className="py-14 rq-light">
        <div className="rq-container">
          <span className="rq-tag">{c.tag}</span>
          <h2 className="font-display mt-3 text-2xl md:text-[34px] font-bold text-white leading-tight">{c.offeringsTitle}</h2>
          <motion.div {...inView} variants={container} className="mt-10 grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
            {c.offerings.map((o, i) => {
              const Icon = ICON_ORDER[i];
              return (
                <motion.div key={i} variants={item} whileHover={{ y: -8 }} className="rq-card p-7 h-full group relative overflow-hidden" data-testid={`cloud-offering-${i}`}>
                  <span className="absolute inset-x-0 top-0 h-[3px] scale-x-0 group-hover:scale-x-100 origin-left transition-transform duration-500" style={{ background: "linear-gradient(90deg,#0b5e2e,#14a24b)" }} />
                  <div className="grid place-items-center h-12 w-12 rounded-xl bg-[rgba(14,122,58,0.12)] text-[color:var(--rq-green)]">
                    <Icon size={24} />
                  </div>
                  <h3 className="font-display mt-5 text-lg font-semibold text-white">{o.title}</h3>
                  <p className="mt-3 text-sm leading-relaxed text-[color:var(--rq-muted)]">{o.desc}</p>
                </motion.div>
              );
            })}
          </motion.div>
        </div>
      </section>

      {/* Benefits */}
      <section className="py-14 rq-greendark">
        <div className="rq-container">
          <span className="rq-tag">{c.value}</span>
          <h2 className="font-display mt-3 text-2xl md:text-[34px] font-bold text-white leading-tight">{c.benefitsTitle}</h2>
          <motion.div {...inView} variants={container} className="mt-8 grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {c.benefits.map((b, i) => (
              <motion.div key={i} variants={item} whileHover={{ x: dir === "rtl" ? -4 : 4 }} className="flex items-start gap-3 rounded-xl border border-[color:var(--rq-border)] bg-[rgba(14,122,58,0.06)] px-4 py-3">
                <span className="grid place-items-center h-6 w-6 rounded-full bg-[rgba(14,122,58,0.16)] text-[color:var(--rq-green)] shrink-0 mt-0.5"><Check size={13} /></span>
                <span className="text-sm leading-relaxed text-white">{b}</span>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      <CTABand />
    </div>
  );
}
